/**
 * 내 결재함 (v1.5.0 · 2026-07-19 메뉴 개편 · v2.50.0 지출결재 추가 ·
 *            v2.115.0 영업팀 월간보고서) — /approvals
 *
 * 두 섹션:
 *   ① 판매보고서 결재 (sales+ — viewer 숨김):
 *     영업진행에서 [판보 저장] 된 건들이 자동 등재.
 *     검토대기 → 담당승인 → 부서장승인 → 최종승인 / 반려. 행 클릭 → 판보 모달.
 *   ② 사내결재 (전 직원 — v2.50.0 · 휴일근무일지 v2.97.0 · 기술지원팀 월간보고서 v2.113.0 ·
 *      영업팀 월간보고서 v2.115.0 · 분기 재고 실사 보고서 v2.224.0):
 *     ⭐**회의록은 v2.229.0 부터 결재가 없다**(참석자 동의 서명) — 결재 목록(group=approval)에서 빠지고,
 *       맨 위 「✍️ 내 서명을 기다리는 회의록」 칸에만 나온다(배지에도 센다 — 내가 처리할 일이라서).
 *     구입지급결의서·개인경비정산서·법인카드사용내역서 + 금액이 없는 보고서류.
 *     검토대기 → 부서장승인(경영지원부는 생략) → 경영지원승인 → 최종승인 / 반려.
 *     ⭐**영업팀 월간보고서·회의록·기술지원팀 월간보고서는 결재판이 3칸**(담당 → 부서장 → 대표이사) — 경영지원 단계가 없다
 *       (2026-09-02·09-03·09-07 사용자 확정). 종류마다 결재선이 갈리므로 안내 문구를 한 줄로 뭉뚱그리지 말 것.
 *       ⚠3칸 목록이 늘어날 때 안내 문구가 「영업팀 월간보고서만」으로 남아 있으면 그날로 틀린 안내가 된다.
 *     가시성은 서버가 행 단위로 거른다 (작성자·결재선 당사자·admin).
 *
 * 기본 화면 = 진행 중만. 완결 모아보기는 문서함 안내.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import * as approvalApi from '../lib/approval-api';
import * as salesApi from '../lib/sales-api';
import * as expenseApi from '../lib/expense-api';
import { SalesReportModal } from '../components/SalesReportModal';
import { ExpenseDocModal, EXPENSE_STATUS_BADGE } from '../components/ExpenseDocModal';
import { HolidayWorkModal } from '../components/HolidayWorkModal';
import { TspReportModal } from '../components/TspReportModal';
import { SalesMonthlyReportModal } from '../components/SalesMonthlyReportModal';
import { DeptMonthlyReportModal } from '../components/DeptMonthlyReportModal';
import { MeetingMinutesModal } from '../components/MeetingMinutesModal';
import { StockAuditReportModal } from '../components/StockAuditReportModal';
import { LastUpdateBadge } from '../components/LastUpdateBadge';
import { useAuth } from '../lib/auth-context';
import type { ReportApprovalStatus } from '../lib/types';
import { SegmentedControl } from '../components/ui/SegmentedControl';
import { LoadingLines } from '../components/ui/Skeleton';

const STATUS_ORDER: ReportApprovalStatus[] = ['검토대기', '담당승인', '부서장승인', '최종승인', '반려'];

const STATUS_BADGE: Record<string, string> = {
  검토대기: 'bg-amber-100 text-amber-800 border-amber-200',
  반려: 'bg-rose-100 text-rose-700 border-rose-200',
  담당승인: 'bg-sky-100 text-sky-800 border-sky-200',
  부서장승인: 'bg-blue-100 text-blue-800 border-blue-200',
  최종승인: 'bg-emerald-100 text-emerald-800 border-emerald-200',
};

function fmtKrw(n: number): string {
  return Math.round(n).toLocaleString('ko-KR');
}
function fmtDateTime(ts: string | null): string {
  if (!ts) return '-';
  const m = ts.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]} ${m[4]}:${m[5]}` : ts;
}

export function ApprovalInboxPage() {
  const { user } = useAuth();
  const isViewer = user?.role === 'viewer';
  // `?tab=report` 로 들어오면 판보 절이 위, 그냥 `/approvals` 면 사내결재 절이 위 (v2.127.0).
  //   지금 이 분기로 들어오는 유일한 입구는 **대시보드 「내 결재 대기」 알림**(`?tab=report&mine=1`).
  //   ⚠분기를 없애지 말 것 — 화면은 하나다(결재함을 쪼개면 대표이사·부장이 두 곳에서 결재해야 한다).
  const [searchParams] = useSearchParams();
  const reportFirst = !isViewer && searchParams.get('tab') === 'report';

  const reportSection = !isViewer && (
    <section key="report">
      <h2 className="ui-modal-title mb-3">📄 판매보고서 결재 <span className="text-sm font-normal text-slate-500">— 영업 판매확정(승인 시 자동 출고)</span></h2>
      <SalesReportInbox />
    </section>
  );
  const expenseSection = (
    <section key="expense">
      <h2 className="ui-modal-title mb-3">🗄 사내결재 (구입지급·개인경비·법인카드·휴일근무일지·부서별 월간보고서·분기 재고 실사)</h2>
      {/* 회의록 동의 서명 (v2.229.0) — 결재는 아니지만 내가 처리할 일이라 맨 위에 */}
      <MeetingSignInbox />
      <ExpenseInbox />
    </section>
  );

  return (
    <div className="p-8">
      <h1 className="ui-page-title">내 결재함</h1>
      <p className="ui-page-sub">
        진행 중인 결재를 한 곳에서 — 완결된 문서는{' '}
        <Link to="/expenses" className="text-blue-600 underline">사내결재 문서함</Link>
        {/* v2.249.0: 판매보고서 보관함은 영업진행 안의 탭으로 옮겼다(viewer 는 판보를 못 본다) */}
        {!isViewer && (
          <>
            , 판매보고서는{' '}
            <Link to="/sales?tab=report" className="text-blue-600 underline">영업진행 › 판매보고서</Link>
          </>
        )}
        에서.
      </p>
      <LastUpdateBadge className="mb-5 mt-1" />
      <div className="space-y-8">
        {/* 판매보고서 결재 — 영업 금액 포함이라 viewer 숨김 (2026-08-04 확정) */}
        {reportFirst ? [reportSection, expenseSection] : [expenseSection, reportSection]}
      </div>
    </div>
  );
}

function SalesReportInbox() {
  // 첫 화면 = 「내 승인 대기」(v2.317.1 · 사용자 「내 결재함에 들어가면 내 승인 대기가 보이게 — 지금은 진행 중」).
  //   대시보드 "내 결재 대기" → /approvals?mine=1 딥링크도 같은 화면. 전에는 진행 중(최종승인 제외)이 기본이었다.
  //   `?mine=1` 은 이제 기본과 같아 더 읽지 않는다(주소는 대시보드·메신저 링크가 계속 쓴다 — 그대로 통한다).
  const [progressOnly, setProgressOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ReportApprovalStatus | null>(null);
  const [mineOnly, setMineOnly] = useState(true);
  const [q, setQ] = useState('');
  const [openSalesId, setOpenSalesId] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['approvals', 'sales-reports', statusFilter, mineOnly, progressOnly, q],
    queryFn: () =>
      approvalApi.listReportApprovals({
        status: mineOnly || progressOnly ? undefined : (statusFilter ?? undefined),
        mine: mineOnly || undefined,
        notFinal: !mineOnly && progressOnly ? true : undefined,
        q: q.trim() || undefined,
      }),
    staleTime: 15_000,
  });

  const rows = listQuery.data?.rows ?? [];
  const counts = listQuery.data?.counts ?? {};
  const mineCount = listQuery.data?.mineCount ?? 0;
  const totalCount = STATUS_ORDER.reduce((a, s) => a + (counts[s] ?? 0), 0);
  const progressCount = totalCount - (counts['최종승인'] ?? 0);

  return (
    <div className="space-y-4">
      {/* 상태 필터 칩 + 검색 */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {/* 거르기 한 벌(v2.252.0 · SegmentedControl) — 내 승인 대기 / 진행 중 / 전체 / 상태별 */}
        <SegmentedControl
          ariaLabel="판매보고서 결재 거르기"
          options={[
            { key: 'mine', label: '🖐️ 내 승인 대기', count: mineCount, tone: 'info' },
            { key: 'progress', label: '진행 중', count: progressCount },
            { key: 'all', label: '전체', count: totalCount },
            ...STATUS_ORDER.map((s) => ({ key: s, label: s, count: counts[s] ?? 0 })),
          ]}
          value={mineOnly ? 'mine' : progressOnly ? 'progress' : (statusFilter ?? 'all')}
          onChange={(k) => {
            setMineOnly(k === 'mine');
            setProgressOnly(k === 'progress');
            setStatusFilter(k === 'mine' || k === 'progress' || k === 'all' ? null : k);
          }}
        />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="매출번호·프로젝트·업체 검색"
          className="ui-input w-64"
        />
      </div>

      {/* 목록 */}
      <div className="bg-white rounded shadow-sm border border-slate-200 overflow-x-auto">
        <table className="data-table w-full text-[13px]">
          <thead>
            <tr>
              <th className="text-center w-24">상태</th>
              <th className="text-center w-28">매출번호</th>
              <th className="text-left">프로젝트명</th>
              <th className="text-left">제안업체 / 엔드유저</th>
              <th className="text-center w-20">담당</th>
              <th className="text-right w-32">제안가</th>
              <th className="text-center w-36">작성 (판보 저장)</th>
              <th className="text-center w-36">최근 처리</th>
            </tr>
          </thead>
          <tbody>
            {listQuery.isLoading && (
              <tr>
                <td colSpan={8} className="text-center text-slate-400 py-10"><LoadingLines /></td>
              </tr>
            )}
            {listQuery.isError && (
              <tr>
                <td colSpan={8} className="text-center text-red-500 py-10">
                  오류: {(listQuery.error as Error).message}
                </td>
              </tr>
            )}
            {listQuery.data && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center text-slate-500 py-10 text-xs leading-relaxed">
                  {mineOnly ? (
                    <>
                      🎉 지금 승인할 차례인 판매보고서가 없습니다.
                      <br />
                      다른 사람의 승인을 기다리는 중이거나, 모두 처리되었습니다.
                    </>
                  ) : progressOnly ? (
                    <>
                      🎉 진행 중인 결재가 없습니다 — 모두 처리되었습니다.
                      <br />
                      결재가 끝난 판매보고서는{' '}
                      <Link to="/sales?tab=report" className="text-blue-600 underline">
                        영업진행 → 판매보고서
                      </Link>{' '}
                      탭에서 모아볼 수 있습니다.
                    </>
                  ) : (
                    <>
                      등재된 판매보고서가 없습니다.
                      <br />
                      영업진행 → 영업건 상세 → [📄 판보 작성] → [💾 판보 저장] 하면 이곳에 [검토대기] 로 올라옵니다.
                    </>
                  )}
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr
                key={r.salesId}
                onClick={() => setOpenSalesId(r.salesId)}
                className="cursor-pointer hover:bg-slate-50"
                title="클릭하여 판매보고서 열기 (검토·승인·수정)"
              >
                <td className="text-center">
                  <span
                    className={`inline-block text-xs px-2 py-0.5 rounded border font-semibold ${STATUS_BADGE[r.status] ?? ''}`}
                  >
                    {r.status}
                  </span>
                </td>
                <td className="text-center font-mono text-xs text-blue-700 font-semibold">
                  {r.salesId}
                  {r.memberCount > 0 && (
                    <div
                      className="inline-block ml-1 text-[11px] px-1 py-0 rounded border border-indigo-200 bg-indigo-50 text-indigo-700 font-semibold align-middle"
                      title={`묶음(합본) 판보 — 함께 묶인 영업건: ${r.memberSalesIds.join(', ')}`}
                    >
                      묶음 +{r.memberCount}
                    </div>
                  )}
                </td>
                <td className="max-w-xs truncate" title={r.projectName ?? ''}>
                  {r.projectName ?? '-'}
                  {r.status === '반려' && r.rejectReason && (
                    <div className="text-xs text-rose-500 truncate" title={r.rejectReason}>
                      ⛔ {r.rejectReason}
                    </div>
                  )}
                </td>
                <td className="text-xs text-slate-600">
                  <div>{r.proposalCompany ?? '-'}</div>
                  <div className="text-slate-400">{r.endUser ?? ''}</div>
                </td>
                <td className="text-center text-xs">{r.empName ?? '-'}</td>
                <td className="text-right num font-semibold">{fmtKrw(r.proposalPrice)}</td>
                <td className="text-center text-xs text-slate-500">
                  {fmtDateTime(r.authoredAt)}
                  {r.authoredByName && (
                    <div className="text-[11px] text-slate-500">{r.authoredByName}</div>
                  )}
                </td>
                <td className="text-center text-xs text-slate-500">
                  {fmtDateTime(
                    r.ceoApprovedAt ?? r.deptApprovedAt ?? r.repApprovedAt ?? r.rejectedAt ?? r.updatedAt,
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-500 leading-snug">
        ※ 결재 흐름: 작성자 작성 → <strong>담당</strong> 승인 → <strong>부서장</strong> 승인 →{' '}
        <strong>대표이사</strong> 승인(최종·확정). 최초 저장 시엔 작성자 서명만 들어가고 담당 승인 단계로 넘어갑니다.
        반려되면 작성자가 수정 후 다시 저장 → 재검토.
        최종승인 후에는 수정이 잠기며 [승인 회수] 로만 되돌릴 수 있습니다.
        목록의 보고서는 영업진행의 판보와 같은 문서라 어느 쪽에서 수정해도 동일하게 반영됩니다.
      </p>

      {/* 판매보고서 모달 — 영업진행과 동일 컴포넌트 (같은 데이터) */}
      {openSalesId && (
        <InboxReportModal salesId={openSalesId} onClose={() => setOpenSalesId(null)} />
      )}
    </div>
  );
}

/** 결재함에서 판보 열기 — 영업 상세와 같은 쿼리키(['salesDetail', id]) 로 캐시 공유 */
function InboxReportModal({ salesId, onClose }: { salesId: string; onClose: () => void }) {
  const detailQuery = useQuery({
    queryKey: ['salesDetail', salesId],
    queryFn: () => salesApi.getSalesDetail(salesId),
    staleTime: 30_000,
  });

  if (detailQuery.isLoading) {
    return (
      <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center">
        {/* 배경 클릭으로 닫지 않으므로(v2.026) 불러오는 중에도 나갈 수단을 남겨 둔다 */}
        <div className="bg-white rounded px-8 py-6 text-slate-500 text-sm">
          판매보고서 불러오는 중…
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary btn-sm ml-4"
          >
            닫기
          </button>
        </div>
      </div>
    );
  }
  if (detailQuery.isError) {
    return (
      <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center">
        <div className="bg-white rounded px-8 py-6 text-sm text-red-600">
          오류: {(detailQuery.error as Error).message}
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary btn-sm ml-4"
          >
            닫기
          </button>
        </div>
      </div>
    );
  }
  if (!detailQuery.data) return null;
  return <SalesReportModal detail={detailQuery.data} onClose={onClose} />;
}

/* ── 지출결재 섹션 (v2.50.0) — 전 직원 ─────────────────── */

const EXPENSE_STATUS_ORDER: expenseApi.ExpenseStatus[] = [
  '검토대기', '부서장승인', '경영지원승인', '최종승인', '반려',
];

/**
 * ⭐문서 종류 → 어느 모달로 열 것인가 — **더하기(허용 목록)** (v2.115.0).
 *
 * ⚠v2.113.0 까지는 「휴일근무일지도 기술지원팀월간보고서도 아니면 지출결재 모달」이라는
 *   **빼기 판정**이었다. 그러면 종류를 하나 늘릴 때마다 새 종류가 조용히 지출결재 모달로
 *   흘러 들어가 금액·계정과목·계좌 칸만 뜬다 — 금액이 없는 문서에는 전부 빈칸이다.
 *   표(Record)로 적으면 종류가 늘어나는 순간 **컴파일러가 여기를 잡아 준다** — 새 종류의
 *   기본값은 「아직 정하지 않음」이지 「지출결재 모달」이 아니다.
 */
type DocModalKind = '지출' | '휴일근무' | '기술월간' | '영업월간' | '부서월간' | '회의록' | '재고실사';
const DOC_MODAL_KIND: Record<expenseApi.ApprovalDocType, DocModalKind> = {
  구입지급결의서: '지출',
  개인경비정산서: '지출',
  법인카드사용내역서: '지출',
  휴일근무일지: '휴일근무',
  기술지원팀월간보고서: '기술월간',
  영업팀월간보고서: '영업월간',
  부서월간보고서: '부서월간',
  회의록: '회의록',
  분기재고실사보고서: '재고실사',
};

/**
 * ⭐금액 칸에 숫자를 찍는 문서 종류 — 역시 **더하기 판정** (v2.115.0).
 *
 * 여기 없는 종류는 「—」. 금액이 없는 문서에 0 을 찍으면 「0원짜리 지출」로 읽힌다.
 * ⚠목록을 손으로 또 적지 않는다 — 지출 3종의 단일 원천(expense-api EXPENSE_DOC_TYPES)에서
 *   만들어 쓴다. 그래야 지출 아닌 종류가 늘어도 여기는 저절로 「—」 쪽에 선다.
 */
const AMOUNT_DOC_TYPES: ReadonlySet<expenseApi.ApprovalDocType> =
  new Set<expenseApi.ApprovalDocType>(expenseApi.EXPENSE_DOC_TYPES);

function ExpenseInbox() {
  // 첫 화면 = 「내 승인 대기」(v2.317.1 · 판매보고서 구역과 같게 · 전에는 진행 중 · `?mine=1` 은 기본과 같아 더 읽지 않는다)
  const [progressOnly, setProgressOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState<expenseApi.ExpenseStatus | null>(null);
  const [mineOnly, setMineOnly] = useState(true);
  const [q, setQ] = useState('');
  /**
   * 결재함에서 연 문서 — **종류에 따라 다른 모달**이 열려야 한다 (v2.97.1).
   * 휴일근무일지를 지출결재 모달로 열면 금액·계정과목 칸만 나오고 근무 내용이 안 보인다.
   */
  const [openDoc, setOpenDoc] = useState<{ id: number; type: expenseApi.ApprovalDocType } | null>(null);

  const listQuery = useQuery({
    queryKey: ['expense', 'inbox', statusFilter, mineOnly, progressOnly, q],
    queryFn: () =>
      expenseApi.listExpenseDocs({
        // ⭐결재함은 **결재를 도는 종류 전부** — 지출결재 3종 + 휴일근무일지 + 보고서류.
        //   이 값을 빼면 기본값(spending)이 걸려 휴일근무일지가 결재함에서 사라진다 (v2.97.1).
        //   ⭐v2.229.0: 'all' → 'approval' — 회의록(동의 서명)의 「검토대기」는 결재 대기가 아니다.
        group: 'approval',
        status: mineOnly || progressOnly ? undefined : (statusFilter ?? undefined),
        mine: mineOnly || undefined,
        notFinal: !mineOnly && progressOnly ? true : undefined,
        q: q.trim() || undefined,
      }),
    staleTime: 15_000,
  });

  // 작성중(임시저장)은 결재함이 아니라 문서함 몫 — 목록·집계에서 뺀다
  const rows = (listQuery.data?.rows ?? []).filter((r) => r.status !== '작성중');
  const counts = listQuery.data?.counts ?? {};
  const mineCount = listQuery.data?.mineCount ?? 0;
  const totalCount = EXPENSE_STATUS_ORDER.reduce((a, s) => a + (counts[s] ?? 0), 0);
  const progressCount = totalCount - (counts['최종승인'] ?? 0);
  // 어느 모달로 열지는 DOC_MODAL_KIND 표 한 곳에서만 정한다 (아래 렌더에서 종류를 다시 따지지 않는다)
  const openKind = openDoc ? DOC_MODAL_KIND[openDoc.type] : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <SegmentedControl
          ariaLabel="사내결재 거르기"
          options={[
            { key: 'mine', label: '🖐️ 내 승인 대기', count: mineCount, tone: 'info' },
            { key: 'progress', label: '진행 중', count: progressCount },
            { key: 'all', label: '전체', count: totalCount },
            ...EXPENSE_STATUS_ORDER.map((s) => ({ key: s, label: s, count: counts[s] ?? 0 })),
          ]}
          value={mineOnly ? 'mine' : progressOnly ? 'progress' : (statusFilter ?? 'all')}
          onChange={(k) => {
            setMineOnly(k === 'mine');
            setProgressOnly(k === 'progress');
            setStatusFilter(k === 'mine' || k === 'progress' || k === 'all' ? null : k);
          }}
        />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="문서번호·작성자·내용 검색"
          className="ui-input w-64"
        />
      </div>

      <div className="bg-white rounded shadow-sm border border-slate-200 overflow-x-auto">
        <table className="data-table w-full text-[13px]">
          <thead>
            <tr>
              <th className="text-center w-24">상태</th>
              <th className="text-center w-32">문서번호</th>
              <th className="text-left w-36">종류</th>
              <th className="text-center w-24">작성자</th>
              <th className="text-center w-28">부서</th>
              <th className="text-center w-24">작성일</th>
              <th className="text-right w-32">금액</th>
              <th className="text-left">내용</th>
              <th className="text-center w-36">최근 처리</th>
            </tr>
          </thead>
          <tbody>
            {listQuery.isLoading && (
              <tr><td colSpan={9} className="text-center text-slate-400 py-10"><LoadingLines /></td></tr>
            )}
            {listQuery.isError && (
              <tr>
                <td colSpan={9} className="text-center text-red-500 py-10">
                  오류: {(listQuery.error as Error).message}
                </td>
              </tr>
            )}
            {listQuery.data && rows.length === 0 && (
              <tr>
                <td colSpan={9} className="text-center text-slate-500 py-10 text-xs leading-relaxed">
                  {mineOnly ? (
                    <>🎉 지금 승인할 차례인 사내결재 문서가 없습니다.</>
                  ) : (
                    <>
                      진행 중인 사내결재 문서가 없습니다.
                      <br />
                      <Link to="/expenses?tab=docs" className="text-blue-600 underline">사내결재 문서함 → 지출결재 문서</Link>{' '}
                      탭에서 구입지급결의서·개인경비정산서를 작성해 제출하면 이곳에 올라옵니다.
                      <br />
                      월간보고서(영업팀·경영지원부·연구소)는{' '}
                      <Link to="/expenses?tab=monthly" className="text-blue-600 underline">사내결재 문서함 → 부서별 월간보고서</Link>{' '}
                      탭의 [📝 보고서 작성]으로 올라옵니다.
                    </>
                  )}
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr
                key={r.id}
                onClick={() => setOpenDoc({ id: r.id, type: r.docType })}
                className="cursor-pointer hover:bg-slate-50"
                title="클릭하여 문서 열기 (검토·승인)"
              >
                <td className="text-center">
                  <span className={`inline-block text-xs px-2 py-0.5 rounded border font-semibold ${EXPENSE_STATUS_BADGE[r.status] ?? ''}`}>
                    {r.status}
                  </span>
                </td>
                <td className="text-center font-mono text-xs text-blue-700 font-semibold">{r.docNo}</td>
                <td className="text-left text-xs">{r.docType}</td>
                <td className="text-center text-xs">{r.authorName}</td>
                <td className="text-center text-xs text-slate-500">{r.authorDept ?? '-'}</td>
                <td className="text-center text-xs text-slate-500">{r.docDate}</td>
                {/* 금액이 없는 문서에 0원을 찍으면 「0원짜리 지출」로 읽힌다 (v2.97.1 · v2.113.0 · v2.115.0) */}
                <td className="text-right num font-semibold">
                  {AMOUNT_DOC_TYPES.has(r.docType)
                    ? fmtKrw(r.totalAmount) : <span className="text-slate-300">—</span>}
                </td>
                <td className="text-xs text-slate-600 max-w-[240px] truncate">
                  {r.purpose ?? r.vendor ?? (r.cardNo ? `카드 ${r.cardNo}` : '-')}
                  {r.status === '반려' && r.rejectReason && (
                    <div className="text-xs text-rose-500 truncate" title={r.rejectReason}>
                      ⛔ {r.rejectReason}
                    </div>
                  )}
                </td>
                <td className="text-center text-xs text-slate-500">{fmtDateTime(r.updatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-500 leading-snug">
        ※ 결재 흐름: 작성자 제출 → <strong>부서장</strong> 승인(경영지원부·무부서 작성자는 생략) →{' '}
        <strong>경영지원</strong> 승인(2명 중 1명) → <strong>대표이사</strong> 승인(최종·확정).
        반려되면 작성자가 수정 후 다시 제출 → 재검토. 최종승인 문서는 사내결재 문서함의 같은 이름 탭(지출결재 문서 / 휴일근무일지 / 부서별 월간보고서 / 분기 재고 실사)에 보관됩니다 — <strong>법인카드사용내역서만 [법인카드 사용내역] 탭</strong>에 보관됩니다.
        <br />
        ※ 기술지원팀 월간보고서는 <strong>기술지원 포털에서 보내 온 문서</strong>라 포털에서 고칠 수 없습니다 — 반려하시면 그쪽에서 새 회차로 다시 올라옵니다.
        <br />
        {/* 결재선이 종류마다 갈린다 — 위 4칸 안내만 두면 3칸 문서 결재자가 「경영지원 차례」를 기다린다 (v2.115.0).
            ⚠v2.118.0 에 회의록, v2.129.0 에 기술지원팀 월간보고서가 3칸 목록에 들어왔다 — 여기 열거는 서버
            NO_SUPPORT_STAGE 와 손으로 맞춘다. 종류를 넣을 때 이 줄을 빼먹으면 그날로 틀린 안내가 된다. */}
        ※ <strong>월간보고서(영업팀·기술지원팀·경영지원부·연구소)·분기 재고 실사 보고서는 결재판이 3칸</strong>입니다 — 작성자(담당) →{' '}
        <strong>부서장</strong> → <strong>대표이사</strong>. <strong>경영지원 승인 단계가 없어</strong>{' '}
        부서장 승인 다음이 곧 최종 승인 차례입니다.
        <br />
        ※ 영업팀 월간보고서는 매출 분석 화면의 [📄 월간보고서] → [결재 상신] 으로 한 달에 한 건 올라오며,
        보고서 숫자는 상신 시점으로 굳어 확정본에 담깁니다(여기서는 고칠 수 없습니다 — 고치려면 반려 후 다시 상신).
        경영지원부·연구소 월간보고서는 사내결재 문서함 [부서별 월간보고서] 탭에서 쓰며, 반려하면 작성자가 고쳐 다시 상신합니다.
        ※ <strong>회의록은 결재가 없습니다</strong>(2026-10 부터) — 사내결재 문서함 [회의록] 탭에서 쓰고, 사내 참석자의 <strong>동의 서명</strong>으로 확정됩니다. 내 서명을 기다리는 회의록은 이 화면 맨 위에 모입니다.
        분기 재고 실사 보고서는 홈 화면 앱(📦 재고 › 분기 재고 실사)에서 실사 담당자가 상신하며, 상신 뒤 실사 기록은 잠깁니다(반려하면 다시 열립니다).
      </p>

      {openDoc && openKind === '휴일근무' && (
        <HolidayWorkModal docId={openDoc.id} onClose={() => setOpenDoc(null)} />
      )}
      {/* 기술지원팀 월간보고서 — 지출결재 모달로 열면 금액·계정과목 칸만 나오고 보고서가 안 보인다 (v2.113.0) */}
      {openDoc && openKind === '기술월간' && (
        <TspReportModal docId={openDoc.id} onClose={() => setOpenDoc(null)} />
      )}
      {/* 영업팀 월간보고서 (v2.115.0) — 지출결재 모달로 열면 이 문서에 없는 금액·계정과목·
          견적서 첨부 칸이 빈 채로 나오고 보고서는 안 보인다. 기술지원팀 월간보고서와 같은 처방. */}
      {openDoc && openKind === '영업월간' && (
        <SalesMonthlyReportModal docId={openDoc.id} onClose={() => setOpenDoc(null)} />
      )}
      {/* 부서별 월간보고서 (v2.280.0) — 같은 처방. 금액이 없는 문서라 지출결재 모달로 열면 안 된다. */}
      {openDoc && openKind === '부서월간' && (
        <DeptMonthlyReportModal docId={openDoc.id} onClose={() => setOpenDoc(null)} />
      )}
      {/* 회의록 (v2.118.0) — 같은 처방. 금액이 없는 문서라 지출결재 모달로 열면 안 된다. */}
      {openDoc && openKind === '회의록' && (
        <MeetingMinutesModal docId={openDoc.id} onClose={() => setOpenDoc(null)} />
      )}
      {/* 분기 재고 실사 보고서 (v2.224.0) — 같은 처방. 금액이 없는 문서라 지출결재 모달로 열면 안 된다. */}
      {openDoc && openKind === '재고실사' && (
        <StockAuditReportModal docId={openDoc.id} onClose={() => setOpenDoc(null)} />
      )}
      {openDoc && openKind === '지출' && (
        <ExpenseDocModal docId={openDoc.id} onClose={() => setOpenDoc(null)} />
      )}
    </div>
  );
}

/**
 * ✍️ 내 서명을 기다리는 회의록 (v2.229.0 · 사용자 요청 「회의록은 결재를 없애고 동의 서명으로」).
 *
 * 결재는 아니지만 「내가 처리할 일」이라 결재함 맨 위에 둔다(사이드바 배지에도 센다).
 * ⭐서버 목록(signMine=1)과 배지(/approvals/pending-count)가 **같은 SQL 조각**을 쓴다 — 숫자와 목록이 갈리지 않는다.
 * 없으면 아무것도 그리지 않는다(결재함이 길어지지 않게). 창을 연 채 서명해 목록이 비어도 창은 닫힐 때까지 둔다.
 */
function MeetingSignInbox() {
  const [openId, setOpenId] = useState<number | null>(null);
  const q = useQuery({
    queryKey: ['expense', 'inbox', 'meeting-sign'],
    queryFn: () => expenseApi.listExpenseDocs({ group: 'meeting', signMine: true }),
    staleTime: 15_000,
  });
  const rows = q.data?.rows ?? [];
  if (rows.length === 0 && openId === null) return null;
  return (
    <div className="mb-5 rounded border border-emerald-200 bg-emerald-50/60 p-3">
      <div className="text-sm font-semibold text-emerald-900 mb-2">
        ✍️ 내 서명을 기다리는 회의록 <span className="tabular-nums">{rows.length}</span>건
        <span className="ml-2 font-normal text-xs text-emerald-700">결재가 아니라 회의 참석자의 동의 서명입니다 — 열어서 읽고 서명하거나 변경 요청을 남겨 주세요</span>
      </div>
      {rows.length > 0 && (
        <div className="overflow-x-auto rounded border border-emerald-100 bg-white">
          <table className="w-full text-[13px]">
            <thead className="bg-emerald-50 text-emerald-900/80">
              <tr>
                <th className="text-left px-3 py-1.5 font-medium w-[120px]">문서번호</th>
                <th className="text-left px-3 py-1.5 font-medium">회의명</th>
                <th className="text-left px-3 py-1.5 font-medium w-[100px]">작성자</th>
                <th className="text-left px-3 py-1.5 font-medium w-[104px]">작성일</th>
                <th className="text-left px-3 py-1.5 font-medium w-[84px]">서명</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} onClick={() => setOpenId(r.id)}
                    className="border-t border-emerald-50 hover:bg-emerald-50/70 cursor-pointer">
                  <td className="px-3 py-1.5 font-mono text-xs">{r.docNo}</td>
                  <td className="px-3 py-1.5 truncate max-w-0">{r.purpose ?? ''}</td>
                  <td className="px-3 py-1.5">{r.authorName}</td>
                  <td className="px-3 py-1.5 text-slate-600">{r.docDate}</td>
                  <td className="px-3 py-1.5 text-xs tabular-nums text-slate-600">
                    {r.meetingSign ? `${r.meetingSign.signed}/${r.meetingSign.required}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {openId !== null && <MeetingMinutesModal docId={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}
